import { useState, useCallback } from 'react'
import { api } from '../api'

// ── Types ────────────────────────────────────────────────────────────────────

type StepName = 'backup' | 'stop' | 'download' | 'extract' | 'start'
type StepState = 'pending' | 'active' | 'done' | 'error'
type UpdateStatus = 'idle' | 'starting' | 'running' | 'done' | 'error'

interface Step {
  name:  StepName
  state: StepState
}

interface InstState {
  status:    UpdateStatus
  commandId: string | null
  steps:     Step[]
  version:   string | null
  error:     string | null
}

const STEP_LABELS: Record<StepName, string> = {
  backup:   'Back up worlds',
  stop:     'Stop service',
  download: 'Download new version',
  extract:  'Extract (preserving worlds & config)',
  start:    'Start service',
}

const STEP_NAMES: StepName[] = ['backup', 'stop', 'download', 'extract', 'start']

function freshSteps(): Step[] {
  return STEP_NAMES.map(name => ({ name, state: 'pending' }))
}

function allDoneSteps(): Step[] {
  return STEP_NAMES.map(name => ({ name, state: 'done' }))
}

function freshInst(): InstState {
  return { status: 'idle', commandId: null, steps: freshSteps(), version: null, error: null }
}

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms))

// ── Sub-components ───────────────────────────────────────────────────────────

function OverallStep({ current }: { current: 1 | 2 | 3 | 4 | 5 }) {
  const labels = ['Paste URL', 'Select instances', 'Confirm', 'Updating', 'Done']
  return (
    <div style={{
      display: 'flex', borderRadius: 8, overflow: 'hidden',
      border: '1px solid var(--border)', marginBottom: 24,
    }}>
      {labels.map((label, i) => {
        const n = i + 1
        const cls = n < current ? 'done' : n === current ? 'active' : ''
        return (
          <div key={n} style={{
            flex: 1, padding: '10px 14px', fontSize: '0.78rem', fontWeight: 600,
            textAlign: 'center',
            background: cls === 'done' ? 'rgba(34,197,94,0.08)'
                      : cls === 'active' ? 'rgba(59,130,246,0.1)' : 'var(--surface)',
            color: cls === 'done' ? 'var(--green)' : cls === 'active' ? 'var(--blue)' : 'var(--muted)',
            borderRight: i < labels.length - 1 ? '1px solid var(--border)' : 'none',
          }}>
            {n < current ? '✓ ' : `${n < 10 ? '0' + n : n} `}{label}
          </div>
        )
      })}
    </div>
  )
}

function InstStepTracker({ steps, status }: { steps: Step[]; status: UpdateStatus }) {
  if (status === 'idle') return null
  return (
    <div style={{
      marginTop: 16, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
    }}>
      {steps.map(s => {
        const isActive = s.state === 'active'
        const isDone   = s.state === 'done'
        const isErr    = s.state === 'error'
        return (
          <div key={s.name} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 13px',
            borderBottom: '1px solid var(--border)',
            background: isActive ? 'rgba(245,158,11,0.05)' : 'var(--bg)',
            color: isDone ? 'var(--green)' : isActive ? 'var(--yellow)' : isErr ? 'var(--red)' : 'var(--muted)',
            fontSize: '0.8rem',
          }}>
            <span style={{ width: 16, textAlign: 'center', flexShrink: 0, fontSize: '0.75rem' }}>
              {isDone ? '✓' : isErr ? '✗' : isActive
                ? <span style={{ display: 'inline-block', animation: 'spin 0.8s linear infinite' }}>◌</span>
                : '○'
              }
            </span>
            {STEP_LABELS[s.name]}
          </div>
        )
      })}
    </div>
  )
}

function InstanceCard({
  num, inst, serverVersion, selected, targetVersion, locked,
  onToggle,
}: {
  num:           1 | 2
  inst:          InstState
  serverVersion: string
  selected:      boolean
  targetVersion: string | null
  locked:        boolean
  onToggle:      () => void
}) {
  const isUpdating = inst.status === 'running' || inst.status === 'starting'
  const isDone     = inst.status === 'done'
  const isError    = inst.status === 'error'
  const showSteps  = inst.status !== 'idle'

  const isStale = targetVersion && serverVersion !== 'unknown' && serverVersion !== targetVersion

  let borderColor = selected ? 'var(--blue)' : 'var(--border)'
  if (isUpdating) borderColor = 'var(--yellow)'
  if (isDone)     borderColor = 'var(--green)'
  if (isError)    borderColor = 'var(--red)'

  const currentVersion = isDone && inst.version ? inst.version : serverVersion

  return (
    <div
      onClick={locked ? undefined : onToggle}
      style={{
        background: selected && !locked ? 'rgba(59,130,246,0.06)'
                  : isDone ? 'rgba(34,197,94,0.05)'
                  : isError ? 'rgba(239,68,68,0.05)' : 'var(--surface)',
        border: `2px solid ${borderColor}`,
        borderRadius: 10, padding: 20,
        cursor: locked ? 'default' : 'pointer',
        position: 'relative',
        transition: 'border-color 0.15s, background 0.15s',
        opacity: locked && inst.status === 'idle' ? 0.5 : 1,
      }}
    >
      {/* Checkbox — hidden during update */}
      {!locked && (
        <div style={{
          position: 'absolute', top: 16, right: 16,
          width: 22, height: 22, borderRadius: 6,
          border: `2px solid ${selected ? 'var(--blue)' : 'var(--border)'}`,
          background: selected ? 'var(--blue)' : 'var(--bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', color: '#fff', transition: 'all 0.15s',
        }}>
          {selected ? '✓' : ''}
        </div>
      )}

      <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 2, paddingRight: locked ? 0 : 32 }}>
        Instance {num}
      </div>
      <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: 14 }}>
        {num === 1 ? 'Permanent world — minecraft-bedrock.service' : 'Switchable worlds — minecraft-bedrock2.service'}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>Current version</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600,
              color: isDone ? 'var(--green)' : isStale ? 'var(--yellow)' : currentVersion === 'unknown' ? 'var(--muted)' : 'var(--text)',
              fontStyle: currentVersion === 'unknown' ? 'italic' : 'normal',
            }}>
              {currentVersion === 'unknown' ? 'not checked' : currentVersion}
            </span>
            {isStale && !isDone && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '1px 7px', borderRadius: 999,
                background: 'rgba(245,158,11,0.12)', color: 'var(--yellow)',
              }}>
                update available
              </span>
            )}
            {isDone && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '1px 7px', borderRadius: 999,
                background: 'rgba(34,197,94,0.12)', color: 'var(--green)',
              }}>
                updated ✓
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>World</span>
          <span style={{ fontSize: '0.85rem' }}>{num === 1 ? 'Bedrock level' : 'Switchable'}</span>
        </div>
      </div>

      {/* Downtime notice (only when idle/selectable) */}
      {inst.status === 'idle' && (
        <div style={{
          marginTop: 14, borderRadius: 8, padding: '8px 12px', fontSize: '0.78rem',
          display: 'flex', alignItems: 'flex-start', gap: 8,
          ...(num === 1
            ? { background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#fcd34d' }
            : { background: 'rgba(59,130,246,0.1)',  border: '1px solid rgba(59,130,246,0.25)',  color: '#93c5fd' }),
        }}>
          <span style={{ flexShrink: 0 }}>{num === 1 ? '⚠' : 'ℹ'}</span>
          <span>{num === 1
            ? 'Service stops ~30 s. Connected players will be briefly disconnected.'
            : 'Service restarts during update. Active world is preserved.'
          }</span>
        </div>
      )}

      {/* Per-instance step tracker */}
      {showSteps && <InstStepTracker steps={inst.steps} status={inst.status} />}

      {/* Error message */}
      {isError && inst.error && (
        <div style={{
          marginTop: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 8, padding: '8px 12px', color: '#fca5a5', fontSize: '0.8rem',
        }}>
          ✗ {inst.error}
        </div>
      )}
    </div>
  )
}

// ── Confirm modal ─────────────────────────────────────────────────────────────

function ConfirmModal({
  open, instances, version, onConfirm, onCancel,
}: {
  open:      boolean
  instances: number[]
  version:   string
  onConfirm: () => void
  onCancel:  () => void
}) {
  if (!open) return null
  const both = instances.length === 2
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100, padding: 20,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: '100%', maxWidth: 460,
      }}>
        <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: 18 }}>
          Confirm update
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: '0.875rem' }}>
          <p>
            Update{' '}
            <strong>{both ? 'both instances' : `Instance ${instances[0]}`}</strong>{' '}
            to version <strong>{version}</strong>.
            {both && ' Instances will update one at a time.'}
          </p>
          <div style={{
            background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
            borderRadius: 8, padding: '10px 14px', color: '#86efac', fontSize: '0.82rem',
            display: 'flex', gap: 8,
          }}>
            <span>🛡</span>
            <span>World backup runs automatically before any files are touched.</span>
          </div>
          <div style={{
            background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)',
            borderRadius: 8, padding: '10px 14px', color: '#fcd34d', fontSize: '0.82rem',
            display: 'flex', gap: 8,
          }}>
            <span>⚠</span>
            <span>
              {both
                ? 'Both servers will stop briefly. Connected players will be disconnected.'
                : instances[0] === 1
                  ? 'Instance 1 will stop for ~30 s. Connected players will be disconnected.'
                  : 'Instance 2 will restart during the update.'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-green" onClick={onConfirm}>Yes, update now</button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Update() {
  const [url,            setUrl]            = useState('')
  const [serverVersions, setServerVersions] = useState<{ instance1: string; instance2: string } | null>(null)
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [selected,       setSelected]       = useState<Set<1 | 2>>(new Set([1, 2]))
  const [inst1,          setInst1]          = useState<InstState>(freshInst())
  const [inst2,          setInst2]          = useState<InstState>(freshInst())
  const [confirmTarget,  setConfirmTarget]  = useState<number[] | null>(null)
  const [running,        setRunning]        = useState(false)

  // ── Derived ──
  const detectedVersion = parseVersion(url)
  const overallStep: 1 | 2 | 3 | 4 | 5 = running
    ? (inst1.status === 'done' && inst2.status === 'done') ||
      (inst1.status === 'done' && !selected.has(2)) ||
      (inst2.status === 'done' && !selected.has(1))
      ? 5 : 4
    : detectedVersion
      ? selected.size > 0 ? 3 : 2
      : 1

  const allDone = (
    (!selected.has(1) || inst1.status === 'done' || inst1.status === 'error') &&
    (!selected.has(2) || inst2.status === 'done' || inst2.status === 'error')
  ) && running

  // ── Handlers ──
  const toggleSelect = (n: 1 | 2) => {
    if (running) return
    setSelected(prev => {
      const next = new Set(prev)
      next.has(n) ? next.delete(n) : next.add(n)
      return next
    })
  }

  const checkVersions = useCallback(async () => {
    setVersionsLoading(true)
    try {
      const v = await api.getServerVersions()
      setServerVersions(v)
    } finally {
      setVersionsLoading(false)
    }
  }, [])

  const setInst = (n: 1 | 2, updater: (prev: InstState) => InstState) => {
    if (n === 1) setInst1(updater)
    else         setInst2(updater)
  }

  const pollInstance = async (n: 1 | 2, commandId: string) => {
    const MAX = 90 // 90 × 4 s = 6 min
    for (let i = 0; i < MAX; i++) {
      await sleep(4000)
      const status = await api.getUpdateStatus(commandId)
      setInst(n, prev => ({
        ...prev,
        steps: status.steps as Step[],
        version: status.version,
      }))
      if (status.status === 'Success') {
        setInst(n, prev => ({ ...prev, status: 'done', steps: allDoneSteps(), version: status.version }))
        return
      }
      if (['Failed', 'Cancelled', 'TimedOut'].includes(status.status)) {
        const errLine = status.stdout.split('\n').filter(l => l.startsWith('ERROR:')).slice(-1)[0] ?? ''
        throw new Error(errLine || 'Update failed on server — check server logs')
      }
    }
    throw new Error('Timed out waiting for update to complete')
  }

  const runUpdate = async (instances: number[]) => {
    setRunning(true)
    setConfirmTarget(null)

    for (const n of instances as (1 | 2)[]) {
      setInst(n, () => ({ status: 'starting', commandId: null, steps: freshSteps(), version: null, error: null }))
      try {
        const result = await api.startUpdate(n, url)
        setInst(n, prev => ({ ...prev, status: 'running', commandId: result.commandId }))
        await pollInstance(n, result.commandId)
      } catch (e) {
        setInst(n, prev => ({ ...prev, status: 'error', error: (e as Error).message }))
        break
      }
    }
  }

  const openConfirm = (instances: number[]) => setConfirmTarget(instances)

  const urlDisabled = running
  const sectionDisabled = !detectedVersion || running

  const ver1 = serverVersions?.instance1 ?? 'unknown'
  const ver2 = serverVersions?.instance2 ?? 'unknown'

  return (
    <>
      <div className="page-header">
        <h1>Server Update</h1>
        <p>Update Instance 1, Instance 2, or both — automatic world backup included</p>
      </div>

      <OverallStep current={overallStep} />

      {/* ── URL card ── */}
      <div className="card mb-4">
        <div className="card-title">Download URL</div>
        <div style={{
          background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)',
          borderRadius: 8, padding: '12px 16px', fontSize: '0.85rem', color: '#93c5fd',
          display: 'flex', gap: 10, marginBottom: 16,
        }}>
          <span style={{ flexShrink: 0 }}>ℹ</span>
          <span>
            Go to the{' '}
            <a
              href="https://www.minecraft.net/en-us/download/server/bedrock"
              target="_blank" rel="noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Bedrock server download page
            </a>
            , accept the EULA, right-click the download button, and copy the link. Paste it here.
          </span>
        </div>
        <div className="form-group">
          <label>Bedrock server .zip URL</label>
          <input
            type="text"
            value={url}
            onChange={e => setUrl(e.target.value)}
            disabled={urlDisabled}
            placeholder="https://minecraft.azureedge.net/bin-linux/bedrock-server-1.XX.XX.XX.zip"
            style={{ fontFamily: 'monospace' }}
          />
        </div>
        {detectedVersion && (
          <div style={{
            marginTop: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
            borderRadius: 8, padding: '8px 14px', color: '#86efac', fontSize: '0.85rem',
            display: 'flex', gap: 8,
          }}>
            <span>✓</span>
            <span>Version detected: <strong>{detectedVersion}</strong></span>
          </div>
        )}
      </div>

      {/* ── Instance selection ── */}
      <div style={{ opacity: sectionDisabled ? 0.4 : 1, pointerEvents: sectionDisabled ? 'none' : 'auto', transition: 'opacity 0.2s' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)' }}>
            Select instances to update
          </span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={checkVersions}
            disabled={versionsLoading || running}
          >
            {versionsLoading ? <><span className="spinning">◌</span> Checking…</> : '↻ Check current versions'}
          </button>
        </div>

        <div className="grid-2">
          <InstanceCard
            num={1} inst={inst1}
            serverVersion={ver1}
            selected={selected.has(1)}
            targetVersion={detectedVersion}
            locked={running}
            onToggle={() => toggleSelect(1)}
          />
          <InstanceCard
            num={2} inst={inst2}
            serverVersion={ver2}
            selected={selected.has(2)}
            targetVersion={detectedVersion}
            locked={running}
            onToggle={() => toggleSelect(2)}
          />
        </div>

        {/* Backup notice */}
        {!running && (
          <div style={{
            marginTop: 14, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
            borderRadius: 8, padding: '12px 16px', color: '#86efac', fontSize: '0.85rem',
            display: 'flex', gap: 10,
          }}>
            <span style={{ flexShrink: 0 }}>🛡</span>
            <span>
              <strong>Full world backup runs automatically first</strong> — worlds are tar'd to{' '}
              <code style={{ fontSize: '0.8em', opacity: 0.85 }}>/usr/games/minecraft/backups/</code>{' '}
              before any files are touched. Roll back any time if something goes wrong.
            </span>
          </div>
        )}

        {/* Action buttons */}
        {!running && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
            <button
              className="btn btn-green"
              disabled={!detectedVersion || !(selected.has(1) && selected.has(2))}
              onClick={() => openConfirm([1, 2])}
            >
              ↑ Update both instances
            </button>
            <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>or</span>
            <button
              className="btn btn-primary btn-sm"
              disabled={!detectedVersion || !selected.has(1)}
              onClick={() => openConfirm([1])}
            >
              Update Instance 1 only
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={!detectedVersion || !selected.has(2)}
              onClick={() => openConfirm([2])}
            >
              Update Instance 2 only
            </button>
          </div>
        )}
      </div>

      {/* Done banner */}
      {allDone && (
        <div style={{
          marginTop: 20, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
          borderRadius: 8, padding: '14px 18px', color: '#86efac', fontSize: '0.9rem',
          display: 'flex', gap: 10,
        }}>
          <span>✓</span>
          <span>
            <strong>Update complete.</strong>{' '}
            Backups saved to{' '}
            <code style={{ fontSize: '0.8em' }}>/usr/games/minecraft/backups/</code>{' '}
            in case you need to roll back.
          </span>
        </div>
      )}

      <ConfirmModal
        open={confirmTarget !== null}
        instances={confirmTarget ?? []}
        version={detectedVersion ?? ''}
        onConfirm={() => runUpdate(confirmTarget ?? [])}
        onCancel={() => setConfirmTarget(null)}
      />
    </>
  )
}

function parseVersion(url: string): string | null {
  const m = url.match(/bedrock-server-([\d.]+)\.zip/)
  return m ? m[1] : null
}
